import { CopyOutlined } from '@ant-design/icons';
import { Input, InputNumber, Modal, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { inviteCodeStore } from '@/stores';
import { inviteAdminStore } from '@/stores/inviteAdminStore';

interface GenerateDialogProps {
  mode?: 'personal' | 'group';
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GenerateDialog({
  open,
  onOpenChange,
  mode = 'personal',
}: GenerateDialogProps) {
  const requestId = useRef(0);
  useEffect(
    () => () => {
      requestId.current++;
    },
    [],
  );
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [codeLabel, setCodeLabel] = useState('');
  const [maxUses, setMaxUses] = useState(50);
  const [count, setCount] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setCount(1);
      setSlug('');
      setName('');
      setCodeLabel('');
      setMaxUses(50);
      setGeneratedCodes([]);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (count < 1) {
      return;
    }

    const current = ++requestId.current;
    setLoading(true);
    try {
      const codes =
        mode === 'group'
          ? [
              (
                await inviteAdminStore.createChannel({
                  slug,
                  name,
                  codeLabel,
                  maxUses,
                })
              ).inviteCode,
            ]
          : (await inviteCodeStore.generateInviteCode({ count })).invite_codes
              .map((inviteCode) => inviteCode.code)
              .filter(Boolean);
      if (codes.length === 0) {
        throw new Error('服务端未返回生成的邀请码');
      }
      if (current !== requestId.current) return;
      setGeneratedCodes(codes);
      message.success(`成功生成 ${codes.length} 个邀请码`);
    } catch {
      if (current !== requestId.current) return;
      message.error(
        mode === 'group'
          ? '生成结果未确认，请刷新团体码列表查看该渠道后再操作'
          : '生成邀请码失败，请重试',
      );
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  };

  const handleCopyAll = async () => {
    if (generatedCodes.length === 0) {
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedCodes.join('\n'));
      message.success(`已复制 ${generatedCodes.length} 个邀请码`);
    } catch (error) {
      console.error('复制邀请码失败:', error);
      message.error('复制失败，请手动选择邀请码');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        if (!loading) {
          onOpenChange(false);
        }
      }}
      closable={!loading}
      keyboard={!loading}
      maskClosable={!loading}
      title={
        generatedCodes.length > 0
          ? '邀请码已生成'
          : mode === 'group'
            ? '生成团体邀请码'
            : '生成邀请码'
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {generatedCodes.length > 0 ? '关闭' : '取消'}
          </Button>
          {generatedCodes.length > 0 ? (
            <Button onClick={handleCopyAll}>
              <CopyOutlined className="mr-2" />
              复制全部
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={
                loading ||
                (mode === 'group' &&
                  (!slug.trim() || !name.trim() || !codeLabel.trim()))
              }
              style={{
                background: 'linear-gradient(135deg, #ff5c5c 0%, #ff7070 100%)',
                color: '#ffffff',
                boxShadow:
                  '0 4px 12px rgba(255, 92, 92, 0.4), 0 0 0 1px rgba(255, 92, 92, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
              className="hover:opacity-90 hover:shadow-lg transition-all font-semibold"
            >
              {loading ? '生成中...' : '生成'}
            </Button>
          )}
        </div>
      }
      centered
      styles={{
        container: {
          padding: '24px',
          borderRadius: '8px',
          backgroundColor: 'var(--card)',
          border: '1px solid var(--border)',
        },
      }}
    >
      {generatedCodes.length > 0 ? (
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            已生成 {generatedCodes.length} 个邀请码，每行一个，可一键复制全部。
          </p>
          <Input.TextArea
            aria-label="本次生成的邀请码"
            value={generatedCodes.join('\n')}
            readOnly
            rows={Math.min(Math.max(generatedCodes.length, 3), 12)}
            className="font-mono"
          />
        </div>
      ) : mode === 'group' ? (
        <div className="space-y-3 py-4">
          <label htmlFor="group-invite-name" className="block space-y-1">
            社区名称
            <Input
              id="group-invite-name"
              aria-label="社区名称"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={128}
            />
          </label>
          <label htmlFor="group-invite-slug" className="block space-y-1">
            渠道标识
            <Input
              id="group-invite-slug"
              aria-label="渠道标识"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="innoai"
              maxLength={64}
            />
          </label>
          <label htmlFor="group-invite-label" className="block space-y-1">
            邀请码品牌
            <Input
              id="group-invite-label"
              aria-label="邀请码品牌"
              value={codeLabel}
              onChange={(e) => setCodeLabel(e.target.value)}
              placeholder="InnoAI"
              maxLength={24}
            />
          </label>
          <label
            htmlFor="group-invite-capacity"
            className="flex items-center justify-between"
          >
            激活名额
            <InputNumber
              id="group-invite-capacity"
              aria-label="激活名额"
              value={maxUses}
              min={1}
              max={1000000}
              precision={0}
              onChange={(value) => {
                if (value !== null) setMaxUses(value);
              }}
            />
          </label>
          <p className="text-sm text-muted-foreground">
            生成一个多人共用的团体码，成功激活新账号才扣名额。
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            请输入要生成的邀请码数量
          </p>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="count">数量</Label>
              <InputNumber
                id="count"
                min={1}
                max={100}
                value={count}
                onChange={(value) => setCount(value || 1)}
                placeholder="请输入数量"
                className="w-full"
              />
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
